import { Text, View } from '@react-pdf/renderer';

import { salesComparisonPageStyles as styles } from '../styles.ts';
import { SalesComparisonAdjustmentKey, SalesComparisonGridColumnData } from '../types.ts';

const Label = ({ text }: { text: string[] }) => {
  const labels = text.map((t) => (
    <View style={{ ...styles.label }} key={`label-${text.join('-')}`}>
      <Text style={styles.text}>{t}</Text>
    </View>
  ));

  return <View style={styles.column}>{labels}</View>;
};

const Value = ({
  comp,
  keys,
  align,
}: {
  comp: SalesComparisonGridColumnData;
  keys: (keyof SalesComparisonGridColumnData)[];
  align: 'left' | 'right' | 'center';
}) => {
  const values = keys.map((t) => (
    <View style={{ ...styles.value, textAlign: align }} key={`label-${keys.join('-')}`}>
      <Text style={styles.text}>{comp?.[t] as string}</Text>
    </View>
  ));

  return <View style={styles.column}>{values}</View>;
};

const Adjustment = ({
  subject,
  comp,
  fields,
  adjustment,
}: {
  subject: SalesComparisonGridColumnData;
  comp: SalesComparisonGridColumnData;
  fields: (keyof SalesComparisonGridColumnData)[];
  adjustment: SalesComparisonAdjustmentKey;
}) => {
  const hasValueDifference = fields.some((k) => {
    const subjectValue = subject?.[k] as string;
    const compValue = comp?.[k] as string;
    return subjectValue && compValue && subjectValue !== compValue;
  });

  const rawAdjustment = (comp?.[adjustment] as string) ?? '';
  const displayAdjustment =
    !rawAdjustment && comp.explicitZeroAdjustments?.[adjustment] && hasValueDifference
      ? '$0'
      : rawAdjustment;

  const negativeAdjustment = displayAdjustment?.startsWith('-');
  return (
    <View style={styles.column}>
      <View style={styles.value}>
        <Text
          style={{
            ...styles.text,
            ...(negativeAdjustment ? styles.negativeAdjustment : styles.positiveAdjustment),
            textAlign: 'right',
          }}
        >
          {displayAdjustment}
        </Text>
      </View>
    </View>
  );
};

const SalesComparisonRow = ({
  fields,
  labels,
  subject,
  comparables,
  adjustment,
  borderTop = false,
  bold = false,
  align = 'left',
}: {
  fields: (keyof SalesComparisonGridColumnData)[];
  labels: string[];
  subject: SalesComparisonGridColumnData;
  comparables: SalesComparisonGridColumnData[];
  adjustment?: SalesComparisonAdjustmentKey;
  borderTop?: boolean;
  bold?: boolean;
  align?: 'left' | 'right' | 'center';
}) => (
  <View
    style={{
      ...styles.row,
      ...(bold ? styles.bold : {}),
    }}
  >
    {[subject, ...comparables].map((comp, i) => (
      <View style={{
        ...styles.innerRow,
        ...(borderTop ? styles.borderTop : {}),
      }} key={`scc-${fields.join('-')}-${comp?.address}`}>
        {i === 0 && <Label text={labels} />}
        <Value comp={comp} keys={fields} align={align} />
        {i !== 0 && adjustment && (
          <Adjustment subject={subject} comp={comp} fields={fields} adjustment={adjustment} />
        )}
      </View>
    ))}
  </View>
);

// eslint-disable-next-line import/no-default-export
export default SalesComparisonRow;
